import { useId } from '@radix-ui/react-id';
import { TriangleAlert } from 'lucide-react';

import { CommandItem } from '../../../components-ui/Command';
import { ContentLabelValue } from '../../../components-ui/ContentLabelValue';
import {
  DropdownMenuGroup,
  DropdownMenuLabel,
  dropdownMenuSectionLabelClasses,
  DropdownMenuSeparator,
} from '../../../components-ui/DropdownMenu';
import { LabelChip } from '../../../components-ui/LabelChip';
import Checkbox from '../../../components/Checkbox';
import {
  CONTENT_LABEL_BUCKET_NAME,
  CONTENT_LABEL_NEEDS_DECISION_ID,
  CONTENT_LABEL_NEEDS_DECISION_NAME,
  CONTENT_LABEL_NULL_BUCKET_ID,
} from '../../../utils/contentLabelBucket';
import type { LabelCategory, LabelDefinition } from '../../settings/labels/types';
import { SearchableFilterSubmenu } from './SearchableFilterSubmenu';

type Props = {
  /** The team's categories in server order, as `useContentLabelCategories` returns them. */
  categories: LabelCategory[];
  /** Category id -> picked value ids, `CONTENT_LABEL_NULL_BUCKET_ID` and `CONTENT_LABEL_NEEDS_DECISION_ID` included. */
  selected: ReadonlyMap<string, ReadonlySet<string>>;
  onToggle: (contentLabelCategoryId: string, valueId: string) => void;
};

const NOTHING_PICKED: ReadonlySet<string> = new Set();

const matchesSubstring = (value: string, search: string) =>
  value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;

/**
 * fig5's LABEL CATEGORIES section of the Content tab's Filter menu: one searchable submenu per category,
 * its values then "Need decision" then "Unlabeled". Both are STRICT, unlike the Performance drawer's bucket:
 * a content that needs a decision comes in only through "Need decision", never through Unlabeled or a value.
 * Every value is offered, counted or not: a just-created label is assigned to nothing until a decision lands.
 */
export function ContentLabelCategoriesFilterSection({ categories, selected, onToggle }: Props) {
  const captionId = useId();
  if (categories.length === 0) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuGroup aria-labelledby={captionId}>
        <DropdownMenuLabel id={captionId} className={dropdownMenuSectionLabelClasses}>
          Label categories
        </DropdownMenuLabel>
        {categories.map((category) => {
          const picked = selected.get(category.labelCategoryId) ?? NOTHING_PICKED;

          return (
            <SearchableFilterSubmenu<LabelDefinition>
              key={category.labelCategoryId}
              triggerLabel={category.name}
              triggerIcon={<CategorySwatch color={category.color} />}
              items={category.labels}
              getKey={(label) => label.labelId}
              getLabel={(label) => label.name}
              isActive={(label) => picked.has(label.labelId)}
              onToggle={(label) => onToggle(category.labelCategoryId, label.labelId)}
              // The submenu is shrink-to-fit, so the chip needs its own ceiling for `truncate` to bite.
              renderItem={(label) => (
                <LabelChip categoryColor={category.color} className="max-w-[240px]">
                  <span className="block max-w-full truncate">{label.name}</span>
                </LabelChip>
              )}
              searchPlaceholder={`Search ${category.name} labels...`}
              fitSearchPlaceholder
              searchThreshold={0}
              filter={matchesSubstring}
              footerSlot={
                <>
                  <NeedDecisionOption
                    checked={picked.has(CONTENT_LABEL_NEEDS_DECISION_ID)}
                    onSelect={() => onToggle(category.labelCategoryId, CONTENT_LABEL_NEEDS_DECISION_ID)}
                  />
                  <UnlabeledOption
                    checked={picked.has(CONTENT_LABEL_NULL_BUCKET_ID)}
                    onSelect={() => onToggle(category.labelCategoryId, CONTENT_LABEL_NULL_BUCKET_ID)}
                  />
                </>
              }
            />
          );
        })}
      </DropdownMenuGroup>
    </>
  );
}

/** GroupByDropdown's 10px category square, centred in the 16px slot the sliders glyph takes elsewhere. */
function CategorySwatch({ color }: { color: string }) {
  return (
    <span aria-hidden className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center">
      <span
        data-testid="content-label-category-swatch"
        className="h-2.5 w-2.5 rounded-[2px]"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

// Built like UnlabeledOption below, for the same two reasons. Text and icon both take fig5's warn
// orange (`orange-400` is #F6820D here, 5.2:1 on the dark menu); the icon stays decorative.
function NeedDecisionOption({ checked, onSelect }: { checked: boolean; onSelect: () => void }) {
  return (
    <CommandItem
      forceMount
      value={CONTENT_LABEL_NEEDS_DECISION_ID}
      aria-label={CONTENT_LABEL_NEEDS_DECISION_NAME}
      onSelect={onSelect}
      className="flex w-full cursor-pointer justify-between"
    >
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[14px] font-medium leading-[1.45] tracking-[0.26px] text-orange-400">
          {CONTENT_LABEL_NEEDS_DECISION_NAME}
        </span>
        <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0 text-orange-400" />
      </span>
      <Checkbox
        style={{ padding: 4 }}
        checked={checked}
        color="dark"
        inputProps={{ 'aria-label': CONTENT_LABEL_NEEDS_DECISION_NAME }}
      />
    </CommandItem>
  );
}

// A cmdk item, not a Radix one, so Radix typeahead cannot pull focus onto it while "u" is typed in
// the search. Its value is the sentinel, so a real label named "Unlabeled" stays a separate option.
function UnlabeledOption({ checked, onSelect }: { checked: boolean; onSelect: () => void }) {
  return (
    <CommandItem
      forceMount
      value={CONTENT_LABEL_NULL_BUCKET_ID}
      aria-label={CONTENT_LABEL_BUCKET_NAME}
      onSelect={onSelect}
      className="flex w-full cursor-pointer justify-between"
    >
      <ContentLabelValue surface="dark" />
      <Checkbox
        style={{ padding: 4 }}
        checked={checked}
        color="dark"
        inputProps={{ 'aria-label': CONTENT_LABEL_BUCKET_NAME }}
      />
    </CommandItem>
  );
}
